import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { SidePanel } from '@saviynt/design-system';
import PropTypes from 'prop-types';

import { setSidePanelVisible } from '../../../redux/certdetails/action';
import {
  CAMPAIGN_TYPES,
  CAMPAIGNTYPE_ENTITLEMENTOWNER,
  MODAL_SIDEPANEL_DIRECTION,
  SCREEN_SIZE_MOBILE_XLARGE,
  SCREEN_SIZE_TABLET_SMALL,
  SCREEN_WIDTH_QUERIES,
} from '../../../utils/constant';
import Carousel from '../../Common/Carousel';

import CertsSidePanelBody from './CertsSidePanelBody';
import CertsSidePanelFooter from './CertsSidePanelFooter';
import DATA from './mockData';
import { getKeysForSidePanelHeader, setIcon } from './utils';

import './CertsSidePanel.css';

function CertsSidePanel({
  data,
  actionsData,
  handleAutoSelectParent,
  handleSidePanelTileButton,
  handleSidePanelBreadCrumbAction,
  checkNodePosition,
  isOpen,
  setMergedLists,
  dataTestId,
}) {
  const [currentTab, setCurrentTab] = useState(null);
  const [isPreviousDisabled, setIsPreviousDisabled] = useState(true);
  const [isNextDisabled, setIsNextDisabled] = useState(true);
  const certsSidePanelRef = useRef();
  const dispatch = useDispatch();

  const {
    global: { mediaQueryValue },
    tabs: { tabData },
    core: { campaignType },
  } = useSelector((state) => state?.certifications?.details);

  const isEOCampaign =
    CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_ENTITLEMENTOWNER;

  const headerObj = getKeysForSidePanelHeader(isEOCampaign, data?.type);
  const nodeId = data?.tracker?.id;

  const handleCloseSidePanel = useCallback(() => {
    dispatch(setSidePanelVisible(false));
  }, [data]);

  // Tablet size Modal list navigaion
  const handlePrevious = () =>
    handleSidePanelTileButton(nodeId, MODAL_SIDEPANEL_DIRECTION.UP);
  const handleNext = () =>
    handleSidePanelTileButton(nodeId, MODAL_SIDEPANEL_DIRECTION.DOWN);

  useEffect(() => {
    if (tabData) {
      const activeTab = tabData.find((tab) => tab.isActive);

      if (activeTab) setCurrentTab(activeTab.key);
    }
  }, []);

  useEffect(() => {
    if (tabData) {
      const activeTab = tabData.find((tab) => tab.isActive);

      if (currentTab && currentTab !== activeTab.key) handleCloseSidePanel();
    }
  }, [tabData, currentTab]);

  useEffect(() => {
    const { isPreviousDisabled: upDisabled, isNextDisabled: downDisabled } =
      checkNodePosition(nodeId);

    setIsPreviousDisabled(upDisabled);
    setIsNextDisabled(downDisabled);
  }, [nodeId]);

  let title = data ? data[headerObj?.title] : '';
  let desc = data ? data[headerObj?.desc] : '';

  if (
    data &&
    data[headerObj?.title] &&
    typeof data[headerObj?.title] === 'string' &&
    data[headerObj?.title]?.includes('<br')
  ) {
    title = data[headerObj?.title]?.replace(/<br\s*\/?>/gm, '\n');
  }

  if (
    data &&
    data[headerObj?.desc] &&
    typeof data[headerObj?.desc] === 'string' &&
    data[headerObj?.desc]?.includes('<br')
  ) {
    desc = data[headerObj?.desc]?.replace(/<br\s*\/?>/gm, '\n');
  }

  const tabletSmallQuery = SCREEN_WIDTH_QUERIES.find(
    (item) => item.label === SCREEN_SIZE_TABLET_SMALL
  );
  const mobileXlargeQuery = SCREEN_WIDTH_QUERIES.find(
    (item) => item.label === SCREEN_SIZE_MOBILE_XLARGE
  );
  const isTabletSmallWidth = tabletSmallQuery.value <= mediaQueryValue;
  const isMobileXlargeWidth = mobileXlargeQuery.value <= mediaQueryValue;

  const certsSidePanelBody = (
    <CertsSidePanelBody
      data={data}
      handleCloseSidePanel={handleCloseSidePanel}
      tabGroupSidePanelRef={certsSidePanelRef}
    />
  );

  const certsSidePanelFooter = (
    <CertsSidePanelFooter
      nodeData={data}
      actionsData={actionsData}
      handleAutoSelectParent={handleAutoSelectParent}
      setMergedLists={setMergedLists}
      isMobileMode={!isTabletSmallWidth || isMobileXlargeWidth}
    />
  );

  const breadCrumbData = [];

  if (
    Array.isArray(data?.tracker?.parents) &&
    data?.tracker?.parents.length > 0
  ) {
    data?.tracker?.parents.forEach((parent) => {
      const parentHeaderObj = getKeysForSidePanelHeader(
        isEOCampaign,
        parent?.type
      );

      breadCrumbData.push({
        text: parent[parentHeaderObj?.title],
        onClick: () => {},
        icon: parent?.type,
        level: parent?.level,
        isActive: false,
        isEndPoint: Boolean(parent?.isEndPoint),
      });
    });
    breadCrumbData.push({
      text: title,
      onClick: () => {},
      icon: data?.type,
      level: data?.tracker?.level,
      isActive: true,
    });
  }

  // TODO: @Jacob, @Sagar, Update to use SidePanel Mobile version (Post DSD-116)
  // TODO: @Jacob, Temp for alertbanners remove post Sidepanel overlay update (ReactDOM.createPortal) DSD-141
  if (isMobileXlargeWidth) {
    return ReactDOM.createPortal(
      <Carousel
        isOpen={isOpen}
        onClose={handleCloseSidePanel}
        header={{
          title: title || '',
          description: desc || '',
          icon: setIcon(data?.type),
        }}
        layout={certsSidePanelBody}
        footer={certsSidePanelFooter}
        breadCrumb={breadCrumbData}
        breadCrumbOnClick={handleSidePanelBreadCrumbAction}
        className='CertsSidePanel'
        layoutClassName='CertsSidePanel-layout'
        isPrevDisabled={isPreviousDisabled}
        isNextDisabled={isNextDisabled}
        onClickNext={handleNext}
        onClickPrev={handlePrevious}
        dataTestId={dataTestId}
      />,
      document.body
    );
  }

  return (
    <SidePanel
      ref={certsSidePanelRef}
      isOpen={isOpen}
      onClose={handleCloseSidePanel}
      header={{
        title: title || '',
        description: desc || '',
        icon: setIcon(data?.type),
      }}
      layout={certsSidePanelBody}
      footer={certsSidePanelFooter}
      isSidePanelNavigatingList
      handlePrevious={handlePrevious}
      handleNext={handleNext}
      isPreviousDisabled={isPreviousDisabled}
      isNextDisabled={isNextDisabled}
      isTabletWidth={isTabletSmallWidth}
      isMobileWidth={isMobileXlargeWidth}
      className='CertsSidePanel'
      classNames={{
        headerClassName: 'CertsSidePanel-headerTitle',
        layoutClassName: 'CertsSidePanel-layout',
      }}
      dataTestId={dataTestId}
    />
  );
}

CertsSidePanel.propTypes = {
  data: PropTypes.shape(DATA),
  actionsData: PropTypes.shape(DATA),
  handleAutoSelectParent: PropTypes.func.isRequired,
  handleSidePanelTileButton: PropTypes.func.isRequired,
  handleSidePanelBreadCrumbAction: PropTypes.func,
  checkNodePosition: PropTypes.func.isRequired,
  isOpen: PropTypes.bool,
  setMergedLists: PropTypes.func.isRequired,
  dataTestId: PropTypes.string,
};

CertsSidePanel.defaultProps = {
  data: null,
  handleSidePanelBreadCrumbAction: () => {},
  actionsData: null,
  isOpen: false,
  dataTestId: null,
};

export default CertsSidePanel;
